import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  signal,
  untracked,
  viewChild,
  DestroyRef,
  type Type,
} from '@angular/core';
import { NgComponentOutlet } from '@angular/common';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { itemWorkspace } from '@modules/common/components/item-workspace/item-workspace-scope';
import { type WorkspaceIdentity } from '@modules/common/services/workspace-identity.service';
import { CommentsService } from '@modules/comments/services/comments.service';
import { Comment } from '@modules/comments/types/comment';
import { SpacesService } from '@modules/settings/services/spaces.service';
import { UtilsModule } from '@modules/utils/utils.module';
import { getIdForSupplementaryData } from '@sharedlib/dtos/dto/StitchItemSupplementaryData';
import type { StitchItemSummaryDto } from '@sharedlib/dtos/dto/StitchItemSummaryDto';
import {
  catchError,
  distinctUntilChanged,
  filter,
  finalize,
  map,
  of,
  shareReplay,
  startWith,
  switchMap,
  take,
  takeUntil,
} from 'rxjs';

/** The rich-text comment API needs escaped plain text, preserving typed tags and line breaks. */
function commentText(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/\n/g, '<br>');
}

export type ItemFooterTab = 'comments' | 'activity';

@Component({
  selector: 'app-item-quick-comment',
  templateUrl: './item-quick-comment.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ReactiveFormsModule, UtilsModule, NgComponentOutlet],
})
export class ItemQuickCommentComponent {
  private static nextFooterId = 0;
  protected readonly footerId = `item-footer-${ItemQuickCommentComponent.nextFooterId++}`;
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly commentsPanel = viewChild<ElementRef<HTMLElement>>('commentsPanel');
  private readonly activityPanel = viewChild<ElementRef<HTMLElement>>('activityPanel');
  private readonly openCommentsControl = viewChild<ElementRef<HTMLElement>>('openCommentsControl');
  private readonly commentsTab = viewChild<ElementRef<HTMLButtonElement>>('commentsTab');
  private readonly activityTab = viewChild<ElementRef<HTMLButtonElement>>('activityTab');
  public readonly revision = input(0);
  public readonly identity = input<WorkspaceIdentity>();
  public readonly item = input<StitchItemSummaryDto>();
  public readonly noun = input('item');
  public readonly selectedTab = input('');
  public readonly openComments = input<() => void>(() => undefined);
  public readonly activityComponent = input<Type<unknown>>();
  public readonly activityInputs = input<Record<string, unknown>>();
  public readonly footerTab = input<ItemFooterTab>('comments');
  public readonly showActivity = input(false);
  public readonly onFooterTabChange = input<(tab: ItemFooterTab) => void>(() => undefined);
  private readonly visitedActivityKey = signal<string | undefined>(undefined);
  protected readonly activityAvailable = computed(() => {
    const item = this.scopedItem();
    const target = this.activityInputs();
    return (
      !!this.activityComponent() &&
      !!target &&
      !!item &&
      item.accountId === this.identity()?.spaceTenantId &&
      target['tenantId'] === item.accountId
    );
  });
  protected readonly activityVisited = computed(
    () => this.visitedActivityKey() === this.lifecycleKey() && this.activityAvailable(),
  );
  private readonly service = inject(CommentsService);
  private readonly spaces = inject(SpacesService);
  private readonly destroyRef = inject(DestroyRef);
  protected readonly draft = new FormControl('', {
    nonNullable: true,
    validators: [Validators.required, Validators.maxLength(4000)],
  });
  protected readonly submitting = signal(false);
  protected readonly scopedItem = computed(() => {
    this.revision();
    return itemWorkspace(this.identity(), this.item()) ? this.item() : undefined;
  });
  /** Capture scalar identity before comparison: a save can mutate the same item object in place. */
  private readonly scopedTarget = computed(() => {
    this.revision();
    const item = this.scopedItem();
    if (!item) return undefined;
    const activity = this.activityInputs();
    const commentId = getIdForSupplementaryData(item);
    const key = JSON.stringify([
      item.accountId,
      item.itemType,
      item.id,
      commentId,
      this.identity()?.spaceTenantId,
      this.identity()?.memberTenantId,
      activity?.['path'],
      activity?.['itemId'],
      activity?.['tenantId'],
    ]);
    return { item, key, commentId };
  });
  private readonly lifecycleKey = computed(() => this.scopedTarget()?.key);
  private readonly target$ = toObservable(this.scopedTarget).pipe(
    distinctUntilChanged((previous, current) => previous?.key === current?.key),
    shareReplay({ bufferSize: 1, refCount: true }),
  );
  private readonly scope$ = this.target$.pipe(map((target) => target?.item));
  protected readonly visible = computed(
    () =>
      !!this.scopedItem() &&
      (!['comments', 'details', 'stitch'].includes(this.selectedTab()) ||
        (this.showActivity() && this.footerTab() === 'activity' && this.activityAvailable())),
  );
  protected readonly placeholder = computed(() => `Comment on this ${this.noun()}`);
  private readonly space = toSignal(
    this.scope$.pipe(
      switchMap((item) =>
        item
          ? this.spaces.getCurrentSpace().pipe(
              switchMap((space) =>
                item.accountId === this.identity()?.spaceTenantId && space?.id !== item.accountId
                  ? this.spaces.getOne(item.accountId)
                  : of(space),
              ),
              catchError(() => of(undefined)),
            )
          : of(undefined),
      ),
    ),
    { initialValue: undefined },
  );
  private readonly recent = toSignal(
    this.target$.pipe(
      switchMap((target) =>
        target
          ? this.service.getCountChanged().pipe(
              filter((id) => id === target.commentId),
              startWith(undefined),
              switchMap(() =>
                this.service.search(target.item, { offset: 0, limit: 8 }).pipe(
                  map((result) => [...result.items].reverse()),
                  catchError(() => of([])),
                ),
              ),
            )
          : of([]),
      ),
    ),
    { initialValue: [] },
  );
  protected readonly comments = computed(() =>
    this.recent().map((comment) => {
      const person = this.space()?.participants?.find((person) => person.id === comment.accountId);
      const author = person?.fullName?.trim() || person?.userName || 'Former member';
      return {
        id: comment.id,
        text: comment.text,
        createdAt: comment.createdAt,
        author,
        avatar: person?.avatar,
        initials: author
          .split(/\s+/)
          .slice(0, 2)
          .map((word) => word[0])
          .join('')
          .toUpperCase(),
      };
    }),
  );
  private draftKey: string | undefined;

  public constructor() {
    effect(() => {
      const visible = this.visible();
      const selected = this.footerTab();
      const active = this.host.nativeElement.ownerDocument.activeElement;
      const inComments =
        this.commentsPanel()?.nativeElement.contains(active) ||
        this.openCommentsControl()?.nativeElement.contains(active);
      const inActivity = this.activityPanel()?.nativeElement.contains(active);
      if (!visible && inActivity) {
        if (active instanceof HTMLElement) active.blur();
      } else if (visible && selected === 'activity' && inComments) {
        this.activityTab()?.nativeElement.focus();
      } else if (visible && selected === 'comments' && inActivity) {
        this.commentsTab()?.nativeElement.focus();
      }
    });
    effect(() => {
      const key = this.lifecycleKey();
      const available = this.activityAvailable();
      const selected = this.footerTab();
      untracked(() => {
        if (key !== this.draftKey) {
          this.draftKey = key;
          this.visitedActivityKey.set(undefined);
          this.draft.reset();
          this.submitting.set(false);
        }
        if (available && selected === 'activity') this.visitedActivityKey.set(key);
      });
    });
  }

  protected selectFooterTab(tab: ItemFooterTab): void {
    this.onFooterTabChange()(tab);
  }

  protected handleFooterKey(event: KeyboardEvent, tab: ItemFooterTab): void {
    if (event.altKey || event.ctrlKey || event.metaKey || !this.activityAvailable()) return;
    let target: ItemFooterTab;
    switch (event.key) {
      case 'ArrowLeft':
      case 'ArrowRight':
        target = tab === 'comments' ? 'activity' : 'comments';
        break;
      case 'Home':
        target = 'comments';
        break;
      case 'End':
        target = 'activity';
        break;
      default:
        return;
    }
    event.preventDefault();
    this.selectFooterTab(target);
    (target === 'comments' ? this.commentsTab() : this.activityTab())?.nativeElement.focus();
  }

  protected showComments(): void {
    this.openComments()();
  }

  protected send(): void {
    const item = this.scopedItem();
    if (!item || this.submitting() || this.draft.invalid || !this.draft.value.trim()) return;
    const key = this.lifecycleKey();
    const comment = new Comment({
      text: commentText(this.draft.value),
      stitchId: getIdForSupplementaryData(item),
      stitchType: item.itemType,
    });
    this.submitting.set(true);
    this.service
      .create(comment, { tenantItems: [item] })
      .pipe(
        take(1),
        takeUntil(this.target$.pipe(filter((target) => target?.key !== key && this.lifecycleKey() !== key))),
        finalize(() => {
          if (this.lifecycleKey() === key) this.submitting.set(false);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: () => {
          if (this.lifecycleKey() === key) this.draft.reset();
        },
        error: () => undefined,
      });
  }
}
